import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Phone, ImageOff } from "lucide-react";
import { WhatsAppIcon } from "./Logo";
import { getTeam, resolveImage } from "../lib/api";
import { TEAM, waLink, telLink } from "../lib/brand";

const card = {
  hidden: { opacity: 0, y: 44 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.75, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Team = () => {
  const [members, setMembers] = useState(TEAM);

  useEffect(() => {
    getTeam()
      .then((data) => {
        if (Array.isArray(data) && data.length) setMembers(data);
      })
      .catch(() => {});
  }, []);

  return (
    <section id="team" className="relative py-16 sm:py-24 lg:py-32 bg-[#f4efdf]" data-testid="team-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <div className="text-center max-w-2xl mx-auto mb-10 sm:mb-16">
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="h-px w-10 bg-[#c5a059]" />
            <span className="text-xs tracking-[0.34em] uppercase text-[#9a7b38] font-semibold">
              The People Behind The Deals
            </span>
            <span className="h-px w-10 bg-[#c5a059]" />
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#131311] leading-[1]">
            Meet the property professionals <span className="italic text-gold-gradient">you can trust.</span>
          </h2>
          <p className="mt-5 text-[#52514c]">
            Real advisors, real relationships — reachable directly, whenever you need them.
          </p>
        </div>

        <div
          className={`grid gap-6 mx-auto ${
            members.length <= 2 ? "sm:grid-cols-2 max-w-3xl" : "md:grid-cols-3"
          }`}
        >
          {members.map((m, i) => (
            <motion.div
              key={m.id}
              custom={i}
              variants={card}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              className="group relative rounded-3xl bg-[#ffffff] border border-[#e7e3d7] p-2 hover:shadow-[0_30px_70px_-40px_rgba(19,19,17,0.4)] transition-shadow duration-500"
              data-testid={`team-member-${m.id}`}
            >
              <div className="relative overflow-hidden rounded-[1.4rem] aspect-[4/5] bg-[#0b0b0a]">
                {m.photo ? (
                  <img
                    src={resolveImage(m.photo)}
                    alt={m.name}
                    className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
                    data-testid={`team-photo-${m.id}`}
                  />
                ) : (
                  <div
                    className="absolute inset-0 grid place-items-center text-center"
                    data-testid={`team-photo-placeholder-${m.id}`}
                  >
                    <div className="flex flex-col items-center gap-3 text-[#6b6558]">
                      <ImageOff className="h-8 w-8" strokeWidth={1.4} />
                      <span className="text-xs tracking-[0.22em] uppercase">Image not available</span>
                    </div>
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0b0b0a] via-[#0b0b0a]/20 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="font-serif text-2xl font-semibold text-[#faf9f5] leading-tight">
                    {m.name}
                  </h3>
                  <p className="text-[#f0d98a] text-sm font-medium">{m.role}</p>
                  <p className="text-[#e7e3d7]/80 text-xs mt-0.5">{m.detail}</p>
                </div>
              </div>

              <div className="p-5">
                <p className="text-sm text-[#52514c] leading-relaxed min-h-[60px]">
                  {m.bio}
                </p>
                <div className="mt-4 flex gap-2.5">
                  <a
                    href={waLink(`Hello ${m.name} from Laxmi Jiwan Properties, I'd like to discuss a property in Hisar.`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#25d366] text-white py-3 text-sm font-semibold hover:bg-[#1eae53] transition-colors"
                    data-testid={`team-whatsapp-btn-${m.id}`}
                  >
                    <WhatsAppIcon className="h-4 w-4" /> Chat
                  </a>
                  <a
                    href={telLink}
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-[#131311] text-[#131311] px-4 py-3 text-sm font-semibold hover:bg-[#131311] hover:text-[#faf9f5] transition-colors"
                    data-testid={`team-call-btn-${m.id}`}
                    aria-label={`Call ${m.name}`}
                  >
                    <Phone className="h-4 w-4" />
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
